/**
 * ResizableMeetingLayout - A 3-column resizable layout for meeting views
 * Supports per-user column width persistence and mobile responsive design
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import api from '../../utils/api';

const MIN_CENTER_WIDTH = 40; // Minimum center column percentage
const MIN_RIGHT_WIDTH = 25;  // Minimum right column percentage
const MAX_LEFT_WIDTH = 30;   // Maximum left column percentage

const ResizableMeetingLayout = ({ 
  leftContent,
  centerContent,
  rightContent,
  showLeft = true,
  className = '',
  defaultWidths = null,
  defaultLeftCollapsed = true,
  maxSideWidthPx = null,
  minRightWidthPercent = MIN_RIGHT_WIDTH
}) => {
  // Layout state
  const expandedLeftWidth = defaultWidths?.left ?? 20;
  const initialLeftWidth = defaultWidths?.left ?? (defaultLeftCollapsed ? 0 : 20);
  const initialCenterWidth = defaultWidths?.center ?? 60;
  const initialRightWidth = defaultWidths?.right ?? (defaultLeftCollapsed ? 40 : 20);

  const [leftCollapsed, setLeftCollapsed] = useState(defaultLeftCollapsed);
  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const [centerWidth, setCenterWidth] = useState(initialCenterWidth);
  const [rightWidth, setRightWidth] = useState(initialRightWidth);
  const [isDragging, setIsDragging] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  
  const containerRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Load user preferences
  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const response = await api.get('/board/layout-prefs');
        if (response.data) {
          setLeftWidth(response.data.left_column_width ?? initialLeftWidth);
          setCenterWidth(response.data.center_column_width ?? initialCenterWidth);
          setRightWidth(response.data.right_column_width ?? initialRightWidth);
          setLeftCollapsed(response.data.left_collapsed ?? defaultLeftCollapsed);
        }
      } catch (err) {
        console.error('Failed to load layout preferences:', err);
      } finally {
        setPrefsLoaded(true);
      }
    };
    loadPrefs();
  }, [defaultLeftCollapsed, initialCenterWidth, initialLeftWidth, initialRightWidth]);

  // Save preferences (debounced)
  const savePrefs = useCallback((newPrefs) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await api.put('/board/layout-prefs', newPrefs);
      } catch (err) {
        console.error('Failed to save layout preferences:', err);
      }
    }, 500);
  }, []);

  // Toggle left sidebar
  const toggleLeft = useCallback(() => {
    const newCollapsed = !leftCollapsed;
    setLeftCollapsed(newCollapsed);
    
    if (newCollapsed) {
      // Collapsing - redistribute space to center
      setCenterWidth(prev => prev + leftWidth);
      setLeftWidth(0);
      savePrefs({ left_collapsed: true, left_column_width: 0, center_column_width: centerWidth + leftWidth, right_column_width: rightWidth });
    } else {
      // Expanding - take space from center
      const newLeftWidth = expandedLeftWidth;
      setLeftWidth(newLeftWidth);
      setCenterWidth(prev => Math.max(MIN_CENTER_WIDTH, prev - newLeftWidth));
      savePrefs({ left_collapsed: false, left_column_width: newLeftWidth, center_column_width: Math.max(MIN_CENTER_WIDTH, centerWidth - newLeftWidth), right_column_width: rightWidth });
    }
  }, [leftCollapsed, leftWidth, centerWidth, rightWidth, savePrefs, expandedLeftWidth]);

  // Handle drag start
  const handleDragStart = useCallback((e, divider) => {
    e.preventDefault();
    setIsDragging(divider);
  }, []);

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const mouseX = e.clientX - containerRect.left;
    const mousePercent = (mouseX / containerWidth) * 100;
    const effectiveMinRightWidthPercent = maxSideWidthPx
      ? Math.min(minRightWidthPercent, (maxSideWidthPx / containerWidth) * 100)
      : minRightWidthPercent;

    if (isDragging === 'left' && !leftCollapsed) {
      // Dragging left divider
      const newLeftWidth = Math.max(5, Math.min(MAX_LEFT_WIDTH, mousePercent));
      const diff = newLeftWidth - leftWidth;
      setLeftWidth(newLeftWidth);
      setCenterWidth(prev => Math.max(MIN_CENTER_WIDTH, prev - diff));
    } else if (isDragging === 'right') {
      // Dragging right divider
      const effectiveLeftWidth = leftCollapsed ? 0 : leftWidth;
      const newCenterWidth = Math.max(MIN_CENTER_WIDTH, mousePercent - effectiveLeftWidth);
      const newRightWidth = 100 - effectiveLeftWidth - newCenterWidth;
      
      if (newRightWidth >= effectiveMinRightWidthPercent) {
        setCenterWidth(newCenterWidth);
        setRightWidth(newRightWidth);
      }
    }
  }, [isDragging, leftCollapsed, leftWidth, maxSideWidthPx, minRightWidthPercent]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(null);
      savePrefs({
        left_column_width: leftCollapsed ? 0 : leftWidth,
        center_column_width: centerWidth,
        right_column_width: rightWidth,
        left_collapsed: leftCollapsed
      });
    }
  }, [isDragging, leftCollapsed, leftWidth, centerWidth, rightWidth, savePrefs]);

  // Mouse event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Don't render until prefs are loaded
  if (!prefsLoaded) {
    return (
      <div className={`flex-1 flex items-center justify-center ${className}`}>
        <div className="animate-pulse text-gray-400">Loading layout...</div>
      </div>
    );
  }

  // Mobile layout - vertical stacking
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  
  if (isMobile) {
    return (
      <div className={`flex flex-col gap-4 ${className}`}>
        {showLeft && leftContent && (
          <div className="order-2">{leftContent}</div>
        )}
        <div className="order-1">{centerContent}</div>
        <div className="order-3">{rightContent}</div>
      </div>
    );
  }

  // Desktop layout - resizable columns
  return (
    <div 
      ref={containerRef}
      className={`flex-1 flex h-full min-h-0 ${className}`}
      data-testid="resizable-meeting-layout"
    >
      {/* Left Column (Quick Links) - Collapsible */}
      {showLeft && (
        <>
          <div 
            className={`relative transition-all duration-200 h-full min-h-0 ${leftCollapsed ? 'w-0 overflow-hidden' : 'overflow-y-auto pr-2'}`}
            style={{ width: leftCollapsed ? 0 : (maxSideWidthPx ? `min(${leftWidth}%, ${maxSideWidthPx}px)` : `${leftWidth}%`) }}
          >
            {!leftCollapsed && leftContent}
          </div>
          
          {/* Left Collapse Toggle */}
          <div className="relative flex items-center">
            <button
              onClick={toggleLeft}
              className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-6 h-12 bg-white border border-gray-200 rounded-md shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors"
              title={leftCollapsed ? "Show Quick Links" : "Hide Quick Links"}
              data-testid="toggle-quick-links"
            >
              {leftCollapsed ? (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronLeft className="h-4 w-4 text-gray-500" />
              )}
            </button>
            
            {/* Left Resize Handle */}
            {!leftCollapsed && (
              <div
                className="w-1 h-full cursor-col-resize hover:bg-purple-200 transition-colors flex items-center justify-center group"
                onMouseDown={(e) => handleDragStart(e, 'left')}
              >
                <GripVertical className="h-6 w-6 text-gray-300 group-hover:text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </div>
        </>
      )}

      {/* Center Column (Agenda) */}
      <div 
        className="flex-1 min-w-0 h-full min-h-0 overflow-y-auto"
        style={maxSideWidthPx ? undefined : { width: `${centerWidth}%` }}
      >
        {centerContent}
      </div>

      {/* Right Resize Handle */}
      <div
        className="w-1 cursor-col-resize hover:bg-purple-200 transition-colors flex items-center justify-center group"
        onMouseDown={(e) => handleDragStart(e, 'right')}
      >
        <GripVertical className="h-6 w-6 text-gray-300 group-hover:text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      {/* Right Column (Meeting Info & Actions) */}
      <div 
        className="min-w-0 h-full min-h-0 overflow-y-auto pl-2"
        style={{ width: maxSideWidthPx ? `min(${rightWidth}%, ${maxSideWidthPx}px)` : `${rightWidth}%` }}
      >
        {rightContent}
      </div>
    </div>
  );
};

export default ResizableMeetingLayout;
